import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, unref as _unref, createBlock as _createBlock, createVNode as _createVNode, createStaticVNode as _createStaticVNode } from "vue"
import _imports_0 from '@/assets/bot_express_logo.png'


const _hoisted_1 = { class: "min-h-screen bg-zinc-900 flex items-center justify-center p-1 sm:p-4 font-nunito" }
const _hoisted_2 = { class: "w-full max-w-lg" }
const _hoisted_3 = { class: "bg-white/5 rounded-xl overflow-hidden" }
const _hoisted_4 = { class: "p-6 sm:p-8" }
const _hoisted_5 = {
  key: 0,
  class: "bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6"
}
const _hoisted_6 = { class: "flex items-center gap-2 text-red-400" }
const _hoisted_7 = { class: "text-sm font-medium" }
const _hoisted_8 = {
  key: 1,
  class: "bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 mb-6"
}
const _hoisted_9 = { class: "flex items-center gap-2 text-emerald-400" }
const _hoisted_10 = { class: "text-sm font-medium" }
const _hoisted_11 = { class: "space-y-4 mb-6" }
const _hoisted_12 = { class: "flex justify-center gap-2 sm:gap-3" }
const _hoisted_13 = ["onUpdate:modelValue", "onInput", "onKeydown", "onFocus", "disabled"]
const _hoisted_14 = { class: "space-y-4" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = { class: "text-center pt-2" }
const _hoisted_17 = ["disabled"]

import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { Loader2 } from 'lucide-vue-next'
import { Toaster } from '@/components/ui/toast'
import type { AxiosError } from 'axios'


export default /*@__PURE__*/_defineComponent({
  __name: 'TwoFactorAuthView',
  setup(__props) {

const { validate2FA, logout: authLogout } = useAuth()
const router = useRouter()

// Refs
const digitInputs = ref<(HTMLInputElement | null)[]>([])
const code = reactive<string[]>(Array(6).fill(''))
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const handleCodeInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const value = input.value

  // Permitir apenas números
  if (!/^\d?$/.test(value)) {
    input.value = ''
    code[index] = ''
    return
  }

  code[index] = value

  // Mover para o próximo input se digitou um número
  if (value && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }

  // Limpar mensagens de erro ao digitar
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

const handleKeyDown = (index: number, event: KeyboardEvent) => {
  const key = event.key

  // Se pressionar backspace e o campo estiver vazio, voltar para o anterior
  if (key === 'Backspace' && !code[index] && index > 0) {
    code[index] = ''
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se pressionar delete, limpar o campo atual
  if (key === 'Delete') {
    code[index] = ''
  }

  // Se pressionar seta para esquerda
  if (key === 'ArrowLeft' && index > 0) {
    setTimeout(() => {
      digitInputs.value[index - 1]?.focus()
    }, 10)
  }

  // Se pressionar seta para direita
  if (key === 'ArrowRight' && index < 5) {
    setTimeout(() => {
      digitInputs.value[index + 1]?.focus()
    }, 10)
  }

  // Permitir colar
  if ((event.ctrlKey || event.metaKey) && key === 'v') {
    return // Permitir o comportamento padrão de colar
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const pastedData = event.clipboardData?.getData('text') || ''
  const numbers = pastedData.replace(/\D/g, '').slice(0, 6)

  // Preencher os campos com os números colados
  for (let i = 0; i < 6; i++) {
    code[i] = numbers[i] || ''
  }

  // Focar no último campo preenchido ou no último campo
  const lastFilledIndex = Math.min(numbers.length, 5)
  setTimeout(() => {
    digitInputs.value[lastFilledIndex]?.focus()
  }, 10)
}

const handleFocus = (index: number) => {
  // Selecionar todo o texto quando focar
  setTimeout(() => {
    digitInputs.value[index]?.select()
  }, 10)
}

const handleVerification = async () => {
  errorMessage.value = ''
  successMessage.value = ''
  isLoading.value = true

  try {
    const response = await validate2FA(code.join(''))

    if (response.token) {
      successMessage.value = 'Código verificado com sucesso!'
      
      setTimeout(() => {
        router.push({ name: 'organizations' })
      }, 500)
    }
  } catch (error) {
    const err = error as AxiosError<{ errors?: { message: string }[], message?: string }>
    const message = err.response?.data?.errors?.map((error) => error.message).join(', ') 
      || err.response?.data?.message 
      || 'Código inválido. Tente novamente.'
    
    errorMessage.value = message
  } finally {
    isLoading.value = false
  }
}

const logout = () => {
  authLogout()
  router.push({ name: 'login' })
}

// Lifecycle
onMounted(() => {
  // Auto-focus no primeiro input
  setTimeout(() => {
    digitInputs.value[0]?.focus()
  }, 100)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "flex items-center justify-center mb-8" }, [
          _createElementVNode("img", {
            src: _imports_0,
            alt: "Logo",
            class: "h-auto w-[180px] sm:w-[280px]"
          })
        ], -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _cache[4] || (_cache[4] = _createStaticVNode("<div class=\"mb-8\" data-v-6c7bd99c><h2 class=\"text-white text-2xl font-bold mb-2\" data-v-6c7bd99c>Verificação em Duas Etapas</h2><p class=\"text-[#888888]\" data-v-6c7bd99c>Insira o código de 6 dígitos do seu App de 2FA</p></div><div class=\"bg-white/5 rounded-xl p-4 border border-white/10 mb-6\" data-v-6c7bd99c><div class=\"flex items-start gap-3\" data-v-6c7bd99c><svg class=\"w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-6c7bd99c><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z\" data-v-6c7bd99c></path></svg><div data-v-6c7bd99c><p class=\"text-sm text-[#AAAAAA]\" data-v-6c7bd99c> Abra o <strong class=\"text-white\" data-v-6c7bd99c>App de 2FA</strong> no seu celular e digite o código de 6 dígitos exibido. </p></div></div></div>", 2)),
            (errorMessage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[0] || (_cache[0] = _createElementVNode("svg", {
                      class: "w-5 h-5 flex-shrink-0",
                      fill: "none",
                      stroke: "currentColor",
                      viewBox: "0 0 24 24"
                    }, [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "stroke-width": "2",
                        d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      })
                    ], -1)),
                    _createElementVNode("span", _hoisted_7, _toDisplayString(errorMessage.value), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
            (successMessage.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _cache[1] || (_cache[1] = _createElementVNode("svg", {
                      class: "w-5 h-5 flex-shrink-0",
                      fill: "none",
                      stroke: "currentColor",
                      viewBox: "0 0 24 24"
                    }, [
                      _createElementVNode("path", {
                        "stroke-linecap": "round",
                        "stroke-linejoin": "round",
                        "stroke-width": "2",
                        d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      })
                    ], -1)),
                    _createElementVNode("span", _hoisted_10, _toDisplayString(successMessage.value), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_11, [
              _cache[2] || (_cache[2] = _createElementVNode("div", { class: "text-center" }, [
                _createElementVNode("label", { class: "text-sm font-medium text-[#AAAAAA]" }, "Código de verificação")
              ], -1)),
              _createElementVNode("div", _hoisted_12, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (digit, index) => {
                  return _withDirectives(_createElementVNode("input", {
                    key: index,
                    ref_for: true,
                    ref: (el) => { digitInputs.value[index] = el as HTMLInputElement | null },
                    "onUpdate:modelValue": ($event: any) => ((code[index]) = $event),
                    type: "text",
                    maxlength: "1",
                    inputmode: "numeric",
                    pattern: "[0-9]*",
                    onInput: ($event: any) => (handleCodeInput(index, $event)),
                    onKeydown: ($event: any) => (handleKeyDown(index, $event)),
                    onPaste: handlePaste,
                    onFocus: ($event: any) => (handleFocus(index)),
                    disabled: isLoading.value,
                    class: _normalizeClass(["w-12 h-14 text-center text-2xl font-semibold bg-white/5 border-2 border-white/10 rounded-lg text-white focus:outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed", {
                  'border-red-500': errorMessage.value,
                  'border-emerald-500': successMessage.value,
                  'animate-pulse': isLoading.value
                }])
                  }, null, 42, _hoisted_13), [
                    [_vModelText, code[index]]
                  ])
                }), 64))
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createElementVNode("button", {
                onClick: handleVerification,
                disabled: isLoading.value || code.join('').length !== 6,
                class: "w-full py-3 px-4 bg-[#10B981] hover:bg-[#0EA271] text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              }, [
                (isLoading.value)
                  ? (_openBlock(), _createBlock(_unref(Loader2), {
                      key: 0,
                      class: "w-4 h-4 animate-spin mr-2"
                    }))
                  : _createCommentVNode("", true),
                _createElementVNode("span", null, _toDisplayString(isLoading.value ? 'Verificando...' : 'Verificar Código'), 1)
              ], 8, _hoisted_15),
              _createElementVNode("div", _hoisted_16, [
                _createElementVNode("button", {
                  onClick: logout,
                  disabled: isLoading.value,
                  class: "text-sm text-[#888888] hover:text-[#AAAAAA] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                }, _cache[3] || (_cache[3] = [
                  _createElementVNode("svg", {
                    class: "w-4 h-4",
                    fill: "none",
                    stroke: "currentColor",
                    viewBox: "0 0 24 24"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "2",
                      d: "M10 19l-7-7m0 0l7-7m-7 7h18"
                    })
                  ], -1),
                  _createTextVNode(" Voltar para o login ")
                ]), 8, _hoisted_17)
              ])
            ]),
            _cache[5] || (_cache[5] = _createStaticVNode("<div class=\"mt-8 pt-6 border-t border-white/10\" data-v-6c7bd99c><div class=\"flex items-start gap-2\" data-v-6c7bd99c><svg class=\"w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5\" fill=\"none\" stroke=\"currentColor\" viewBox=\"0 0 24 24\" data-v-6c7bd99c><path stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"2\" d=\"M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.348 16.5c-.77.833.192 2.5 1.732 2.5z\" data-v-6c7bd99c></path></svg><p class=\"text-xs text-[#666666]\" data-v-6c7bd99c> Não consegue validar o código de 2 fatores? Entre em contato com o suporte. </p></div></div>", 1))
          ])
        ])
      ])
    ]),
    _createVNode(_unref(Toaster))
  ], 64))
}
}

})